import {BOX_HEIGHTS, BOX_WIDTHS, cardBoxSizes} from '@hconnect/uikit'
import {useMediaQuery, useTheme} from '@mui/material'
import {DowntimeByCategoryChartContainer} from '@widgets/downtime-analytics'
import {OperationHoursFromYesterdayContainer} from '@widgets/running-times'
import {MyTasksSummaryContainer} from '@widgets/tasks'
import React from 'react'

import {
  KilnStatusMonitoringContainer,
  ReliabilityPlantStatusDailyKpisContainer,
  ReliabilityPlantStatusKpisContainer,
  WorkOrdersContainer,
  RcfaContainer
} from '../containers'
import {PlantDashboardDesktopLayout, PlantDashboardMobileLayout} from '../layouts'

type Props = {
  plantId: string
  titleSlot?: React.ReactNode
  aboveTitleSlot?: React.ReactNode
  maintainHref?: string
  myTasksHref?: string
  plantStatusPageHref?: string
  cockpitWorkOrdersHref?: string
  onEquipmentClick?: (equipmentId: string) => void
  onUserViewsKmsWidget?: () => void
}

export const ReliabilityEngineerPlantDashboard = ({
  plantId,
  titleSlot,
  aboveTitleSlot,
  maintainHref,
  myTasksHref,
  plantStatusPageHref,
  cockpitWorkOrdersHref,
  onEquipmentClick,
  onUserViewsKmsWidget
}: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = !useMediaQuery(theme.breakpoints.down('sm'))
  const isLarge = !useMediaQuery(theme.breakpoints.down('lg'))

  if (isMobile) {
    return (
      <PlantDashboardMobileLayout
        titleSlot={titleSlot}
        aboveTitleSlot={aboveTitleSlot}
        isTablet={isTablet}
        kilnMonitoringSlot={
          <KilnStatusMonitoringContainer
            plantId={plantId}
            cardBoxSx={{width: '100%'}}
            placeholderWrapperSx={{width: '100%'}}
            onSelectedKilnChange={onUserViewsKmsWidget}
          />
        }
        workOrdersSummarySlot={
          <WorkOrdersContainer
            plantId={plantId}
            cardBoxSx={{height: BOX_HEIGHTS['M']}}
            cockpitWorkOrdersHref={cockpitWorkOrdersHref}
          />
        }
        plantStatusKpisSlot={
          <ReliabilityPlantStatusDailyKpisContainer
            plantId={plantId}
            plantStatusPageHref={plantStatusPageHref}
          />
        }
        operationHoursSlot={
          <OperationHoursFromYesterdayContainer
            plantId={plantId}
            onEquipmentClick={onEquipmentClick}
            cardBoxSx={{width: '100%'}}
            isMobile={true}
          />
        }
        rcfaSlot={
          <RcfaContainer
            plantId={plantId}
            linkPath={maintainHref}
            cardBoxSx={{height: BOX_HEIGHTS['S']}}
            placeholderWrapperSx={{width: '100%', height: '100%'}}
          />
        }
        myTasksSlot={
          <MyTasksSummaryContainer
            plantId={plantId}
            cardBoxSx={{height: BOX_HEIGHTS['S']}}
            placeholderWrapperSx={{width: '100%', height: '100%'}}
            myTasksHref={myTasksHref}
          />
        }
        downtimeByCategorySlot={
          <DowntimeByCategoryChartContainer
            plantId={plantId}
            cardBoxSx={{
              width: '100%',
              height: BOX_HEIGHTS['M']
            }}
            placeholderWrapperSx={{width: '100%', height: '100%'}}
          />
        }
      />
    )
  }

  return (
    <PlantDashboardDesktopLayout
      titleSlot={titleSlot}
      aboveTitleSlot={aboveTitleSlot}
      isLarge={isLarge}
      kilnMonitoringSlot={
        <KilnStatusMonitoringContainer
          plantId={plantId}
          cardBoxSx={{
            height: {sm: '100%', lg: BOX_WIDTHS['M']},
            width: {sm: '100%', lg: BOX_WIDTHS['M']}
          }}
          onSelectedKilnChange={onUserViewsKmsWidget}
        />
      }
      workOrdersSummarySlot={
        <WorkOrdersContainer
          plantId={plantId}
          cardBoxSx={cardBoxSizes['M']}
          cockpitWorkOrdersHref={cockpitWorkOrdersHref}
        />
      }
      plantStatusKpisSlot={
        <ReliabilityPlantStatusKpisContainer
          plantId={plantId}
          plantStatusPageHref={plantStatusPageHref}
        />
      }
      operationHoursSlot={
        <OperationHoursFromYesterdayContainer
          plantId={plantId}
          onEquipmentClick={onEquipmentClick}
          cardBoxSx={cardBoxSizes['M']}
        />
      }
      rcfaSlot={
        <RcfaContainer
          plantId={plantId}
          linkPath={maintainHref}
          cardBoxSx={{...cardBoxSizes['S'], width: {sm: BOX_WIDTHS['S'], md: '100%'}}}
          placeholderWrapperSx={{width: '100%', height: '100%'}}
        />
      }
      myTasksSlot={
        <MyTasksSummaryContainer
          plantId={plantId}
          cardBoxSx={{...cardBoxSizes['S'], width: {sm: BOX_WIDTHS['S'], md: '100%'}}}
          placeholderWrapperSx={{width: '100%', height: '100%'}}
          myTasksHref={myTasksHref}
        />
      }
      downtimeByCategorySlot={
        <DowntimeByCategoryChartContainer
          plantId={plantId}
          cardBoxSx={{
            width: '100%',
            height: BOX_HEIGHTS['M']
          }}
          placeholderWrapperSx={{height: '100%'}}
        />
      }
    />
  )
}
